import type { Styles } from '@adornis/ass/style.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-vr.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { BaseError } from '@adornis/validation/errors/BaseError.js';
import type { OSCE } from '@ils/osces/db/osce.js';
import { getNextSlotForEvaluation, getSlotForRating } from '@ils/ratings/api/evaluation.js';
import { commitRatingDoc, subscribeAndCreateMyRatingDocForSlot } from '@ils/ratings/api/ratings.js';
import { ExamSlotRatingDoc } from '@ils/ratings/db/rating.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import {
  catchError,
  distinctUntilKeyChanged,
  filter,
  firstValueFrom,
  fromEvent,
  of,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';
import { Answer } from '../../../db/answer.js';
import type { ILSUser } from '../../../db/ils-user.js';
import '../../components/ils-loader.js';
import { slotSelector } from './evaluation-slot-selector.js';

@customElement('ils-evaluation-async')
export class ILSEvaluationAsync extends ChemistryLitElement {
  @property({ attribute: false }) slotID = new RXController<Maybe<string>>(this, null);

  @state() private readonly _myRating = new RXController(
    this,
    this.slotID.observable.pipe(
      filter(Boolean),
      switchMap(slotID =>
        subscribeAndCreateMyRatingDocForSlot(slotID)(ExamSlotRatingDoc.allFields).pipe(
          catchError(error => {
            return of({
              _id: 'NONE_GIVEN',
              slotID,
              error,
              alreadyExists: error instanceof BaseError && error.extensions.code === 403,
            });
          }),
        ),
      ),
    ),
  );

  @state() examSlot = new RXController(
    this,
    this._myRating.observable.pipe(
      filter(Boolean),
      distinctUntilKeyChanged('_id'),
      switchMap(rating => getSlotForRating(rating._id)(slotSelector)),
    ),
  );

  @state() answer = new RXController(
    this,
    this.examSlot.observable.pipe(
      filter(Boolean),
      switchMap(examSlot =>
        examSlot.answer?._id ? subscribeByID(Answer, examSlot.answer?._id)(slotSelector.answer) : of(null),
      ),
    ),
  );

  @state() private _selectedRatingView: 'rating' | 'tasks' = 'rating';
  @state() private readonly _currentUser = new RXController(this, currentUser<ILSUser>());

  private readonly _gridRef = createRef<HTMLDivElement>();
  private readonly _dragRef = createRef<HTMLDivElement>();
  @state() private _dragging = false;
  @state() private _currentSize = 0;
  private _dragStart = 0;
  private _dragX = 0;

  override connectedCallback(): void {
    super.connectedCallback();
    fromEvent<MouseEvent>(document, 'mousemove')
      .pipe(takeUntil(this.disconnected))
      .subscribe(e => {
        if (!this._dragging) return;
        this._dragX = e.clientX;
        this._updateLayout();
        e.preventDefault();
      });

    fromEvent<MouseEvent>(document, 'mouseup')
      .pipe(takeUntil(this.disconnected))
      .subscribe(_ => {
        if (!this._dragging) return;
        this._dragging = false;
      });
  }

  override render() {
    const user = this._currentUser.value;
    const osce = this.examSlot.value?.osce;
    const rating = this._myRating.value;
    const answer = this.answer.value;
    if (this.examSlot.isLoading) return this._loader('');
    if (!(rating instanceof ExamSlotRatingDoc)) return this._errorState(rating);
    if (!this.examSlot.value || !osce) return html`<x-text> keine Aufgabendefinition gefunden </x-text>`;
    if (!user || !rating || !answer) return this._loader('Erstelle Bewertungsbogen...');

    return html`
      <x-flex padding="sm" h100>
        ${this._header(osce)}
        <div grid-container ${ref(this._gridRef)}>
          <x-flex
            padding="md"
            space="md"
            flex
            ${css({
              overflow: 'auto',
              maxHeight: 'calc(100vh - 150px)',
            })}
          >
            ${this._ratingSection(osce, rating, answer)}
            ${rating.committed || answer.reportedError ? this._nextRatingButtons() : this._submitButton(rating)}
          </x-flex>
          <x-flex
            size-bar
            crossaxis-center
            ?dragging=${this._dragging}
            ${ref(this._dragRef)}
            @mousedown=${(e: MouseEvent) => {
              this._dragging = true;
              this._dragStart = e.clientX;
            }}
          >
            <x-vr></x-vr>
          </x-flex>
          <!-- Media (Audio & Text Notes), with the inspector's main note -->
          <ils-slot-detail
            ?rating-finished=${rating.committed || !!answer.reportedError}
            .rating=${this._myRating.value}
            .examSlot=${this.examSlot.value}
            ${css({
              position: 'sticky',
              top: this.spacing.sm,
              overflow: 'auto',
              maxHeight: 'calc(100vh - 150px)',
            })}
            @report-error=${() => this.shadowRoot?.getElementById('header')?.scrollIntoView({ behavior: 'smooth' })}
          ></ils-slot-detail>
        </div>
      </x-flex>
    `;
  }

  private _header(osce: OSCE) {
    return html`
      <x-flex id="header" horizontal crossaxis-center space="sm">
        <x-icon
          ${css({ fontSize: '28px', cursor: 'pointer' })}
          @click=${() => goTo({ path: '/start/evaluation-overview', searchParams: NO_CHANGE })}
        >
          arrow_left_alt
        </x-icon>
        <x-h1> ${osce.titleTrainee} </x-h1>
      </x-flex>
    `;
  }

  private _ratingSection(
    osce: OSCE,
    rating: ExamSlotRatingDoc,
    answer: NarrowedEntity<Answer, typeof slotSelector.answer>,
  ) {
    if (answer.reportedError)
      return html`
        <x-text>
          Es wurde ein Fehler in der Antwort zu dieser Situation gemeldet, Bewertung ist nicht möglich bis ein Admin die
          Antwort prüft.
        </x-text>
      `;

    return html`
      <x-tabs
        flex-none
        .selected=${this._selectedRatingView}
        @selected-changed=${(e: ValueEvent<'rating' | 'tasks'>) => (this._selectedRatingView = e.detail.value)}
      >
        <x-tab label="rating"> Bewertung </x-tab>
        <x-tab label="tasks"> Aufgabenstellung </x-tab>
      </x-tabs>
      <ils-evaluation-rating
        ${css({ display: this._selectedRatingView === 'rating' ? 'block' : 'none' })}
        .rating=${rating}
      ></ils-evaluation-rating>
      <ils-view-osce
        flex
        ${css({ display: this._selectedRatingView === 'rating' ? 'none' : 'block' })}
        .osce=${osce}
      ></ils-view-osce>
    `;
  }

  private _submitButton(rating: ExamSlotRatingDoc) {
    return html`
      <x-button
        ${css({ alignSelf: 'end' })}
        ?disabled=${!rating.ratings.every(rating => rating.score !== -1)}
        @click=${async () => {
          await XNativeDialog.guard(() => Promise.all([commitRatingDoc(rating._id)(), firstValueFrom(timer(1000))]), {
            errorMessagePrefix: 'Fehler beim Absenden der Bewertung',
          });
        }}
      >
        Bewertung abgeben
      </x-button>
    `;
  }

  private _nextRatingButtons() {
    return html`
      <x-flex horizontal end space-between space="md" padding="lg">
        <x-button @click=${() => goTo({ path: '/start/evaluation-overview', searchParams: NO_CHANGE })}>
          Zurück zur Übersicht
        </x-button>
        <x-button
          tone="error"
          mode="outline"
          @click=${async () => {
            const action = async () => {
              this.shadowRoot?.getElementById('header')?.scrollIntoView({ behavior: 'smooth' });
              const nextSlot = await getNextSlotForEvaluation()({ _id: 1 });
              if (!nextSlot) goTo({ path: '/start/evaluation-overview', searchParams: NO_CHANGE });
              else goTo({ path: `/start/evaluation-async/${nextSlot._id}`, searchParams: NO_CHANGE });
            };
            await XDialog.waitFor(action(), 'Nächste Bewertung wird initialisiert...');
          }}
        >
          Nächste Bewertung starten
        </x-button>
      </x-flex>
    `;
  }

  private _errorState(errorState: Maybe<{ _id: string; slotID: string; error: Error; alreadyExists: boolean }>) {
    return html`
      <x-flex padding="lg" space="ms">
        <x-text>
          Ein anderer Bewerter mit der gleichen Spezialisierung wie Sie bearbeitet diese Situation bereits. Bitte wählen
          Sie eine andere Situation.
        </x-text>
        ${this._nextRatingButtons()}
      </x-flex>
    `;
  }

  private _loader(text: string) {
    return html`
      <x-flex space="lg" w100 h100 center crossaxis-center> <ils-loader> </ils-loader> <x-h3> ${text} </x-h3> </x-flex>
    `;
  }

  private _ensureGridPixelValues() {
    const grid = this._gridRef.value;
    if (!grid) return;
    const [ratingSection, divider, mediaSection] = grid.children;
    if (!ratingSection || !divider || !mediaSection || this._currentSize !== 0) return;
    this._currentSize = ratingSection.clientWidth;
    grid.style.setProperty('grid-template-columns', `${this._currentSize}px max-content 1fr`);
  }

  private _updateLayout() {
    const grid = this._gridRef.value;
    const drag = this._dragRef.value;
    if (!grid || !drag) return;
    this._ensureGridPixelValues();
    const diff = this._dragX - this._dragStart;
    this._currentSize = this._currentSize + diff;
    this._dragStart = this._dragStart + diff;
    grid.style.setProperty('grid-template-columns', `${this._currentSize}px max-content 1fr`);
  }

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        ':host': {
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
        },
        '[size-bar]': {
          cursor: 'grab',
          padding: `0 ${this.spacing.xs}`,
          '&[dragging]': { cursor: 'grabbing' },
          '&:hover > x-vr': { background: this.colors.neutral.shade(50) },
        },
        '[grid-container]': {
          display: 'grid',
          gridTemplateColumns: '5fr max-content 2fr',
        },
      },
    ];
  }
}
